"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getLookaheadPath = getLookaheadPath;
exports.getPathTakenThroughSimulation = getPathTakenThroughSimulation;
exports.getTimelineOfMissionThroughSimulation = getTimelineOfMissionThroughSimulation;
var _notableEvents = require("../constants/notableEvents");
const MAX_CONSECUTIVE_MISSING_CRITICAL_ACTION_BEFORE_ASSUME_LOST = 5;

/**
 * getTimelineOfMissionThroughSimulation - get a timeline (an array of notable events) of the what a mission has gone through and what is expected to occur
 * until the end of the mission. The timeline for a mission will change as more actions are ran on the mission, this is because there are multiple paths
 * a mission can take through a simulation. This function chooses the timeline where the expected time until the end of the mission is the largest (excluding cycles).
 *
 * @param {CondensedActionGraph<IdType>} condensedActionGraph - the condensed action graph for the simulation being ran
 * @param {string[]} actionsSeenInOrder - the game (simulation) action ran on the mission in the order that they were ran
 * @param {TimelineResult<IdType>|null} previousTimelineResult - the previous result of this function call, null if not available
 *
 * @return {TimelineResult<IdType>}
 */
function getTimelineOfMissionThroughSimulation(condensedActionGraph, actionsSeenInOrder, previousTimelineResult) {
  const pathTakenResult = getPathTakenThroughSimulation(condensedActionGraph, actionsSeenInOrder, previousTimelineResult?.pathTakenResult ?? null);
  if (!pathTakenResult) {
    return {
      pathTakenResult: null,
      timeline: null
    };
  }
  const lookaheadPath = getLookaheadPath(condensedActionGraph, pathTakenResult);
  return {
    pathTakenResult,
    timeline: lookaheadPath ?? null
  };
}

/**
 * getPathTakenThroughSimulation - get the most likely path a mission has taken through the simulations condensedActionGraph. This function finds the path taken by looking at the ran actions
 * which that are necessary for a condensedActionGraphNode to approach their outcomes (ie. children). If the actions ran by the mission makes the path taken ambiguous (ie. there are multiple possible paths),
 * this function will choose a path and try to keep to that path (hence taking in the function's previous result) until a non-ambiguous path can be found.  In the case that the
 * mission takes a path is taken which is not known by the condensedActionGraph, this function will try to find a path in which the most recent actions are placed in a condensedNode. In the case where
 * no path could be determined, this function will return null.
 *
 * @param {CondensedActionGraph<IdType>} condensedActionGraph - the condensed node graph for the simulation
 * @param {string[]} actionsSeenInOrder - the game (simulation) action ran on the mission in the order that they were ran
 * @param {PathTakenResult<IdType>|null} previousPathTakenResult - the previous result of this function call, null if not available
 *
 * @return {?PathTakenResult<IdType>}
 */
function getPathTakenThroughSimulation(condensedActionGraph, actionsSeenInOrder, previousPathTakenResult) {
  let possiblePathNodes = [];
  let nextPossiblePathNodes = [];
  const nodeLookup = {};
  for (let condensedNode of condensedActionGraph.nodes) {
    nodeLookup[condensedNode.id] = condensedNode;
  }
  const startingNode = nodeLookup[condensedActionGraph.startingNodeId];
  if (!startingNode) {
    return null;
  }

  // TODO: if we have the a previousPathTakenResult, use it as the start position of the path generation if possible
  possiblePathNodes.push({
    // start at the beginning of the simulation
    node: startingNode,
    startNodeAtTime: 0,
    startNodeAtNotableEventIndex: 0,
    path: [startingNode.id],
    lastSeenCriticalActionId: null,
    ...getCheckpointAndChildrenInformationForPathAtCondensedNode(startingNode, null, nodeLookup)
  });
  let consecutiveUnplacedCriticalActionCount = 0;
  for (let seenActionId of actionsSeenInOrder) {
    if (!condensedActionGraph.criticalActionIdToCondensedNodeIds.hasOwnProperty(seenActionId)) {
      continue;
    }
    for (let possiblePath of possiblePathNodes) {
      if (possiblePath.node.criticalPathActions.hasOwnProperty(seenActionId)) {
        // we are still in the node
        // We could check that the critical actions are happening in the expected order, but due to the times in actions being estimates, that could cause some race conditions
        possiblePath.lastSeenCriticalActionId = seenActionId;
        nextPossiblePathNodes.push(possiblePath);
        continue;
      }
      for (let possibleNextNode of possiblePath.possibleNextCondensedNodes) {
        if (!possibleNextNode.criticalPathActions.hasOwnProperty(seenActionId)) {
          // remove traceNode by not moving it to `nextTracingNodes`
          continue;
        }
        nextPossiblePathNodes.push({
          node: possibleNextNode,
          path: [...possiblePath.path, possibleNextNode.id],
          startNodeAtTime: 0,
          startNodeAtNotableEventIndex: 0,
          lastSeenCriticalActionId: seenActionId,
          ...getCheckpointAndChildrenInformationForPathAtCondensedNode(possibleNextNode, possiblePath, nodeLookup)
        });
      }
    }
    if (!nextPossiblePathNodes.length) {
      // The action was somehow not a critical action in this context. This can occur if the graph was generated incorrectly or the simulation changed during the mission (ie. the simulation was updated while it was SAVED_FOR_LATER)
      consecutiveUnplacedCriticalActionCount += 1;
      if (consecutiveUnplacedCriticalActionCount >= MAX_CONSECUTIVE_MISSING_CRITICAL_ACTION_BEFORE_ASSUME_LOST) {
        // We are completely lost. Jump to the condensed node where the current actionId exists in the critical path
        const actionExistsInCondensedNodes = condensedActionGraph.criticalActionIdToCondensedNodeIds[seenActionId];
        if (!actionExistsInCondensedNodes) {
          continue;
        }
        for (let condensedNodeId of actionExistsInCondensedNodes) {
          const condensedNode = nodeLookup[condensedNodeId];
          if (!condensedNode) {
            continue;
          }
          nextPossiblePathNodes.push({
            node: condensedNode,
            path: [
            // we do not know what nodes came before, consider this the first node
            condensedNode.id],
            lastSeenCriticalActionId: seenActionId,
            ...startCondensedNodeAtAction(condensedNode, seenActionId),
            ...getCheckpointAndChildrenInformationForPathAtCondensedNode(condensedNode, null, nodeLookup)
          });
        }
      }
    }
    if (!nextPossiblePathNodes.length) {
      // we do not know what the next paths are, assume the current paths are the best guess
      continue;
    }
    consecutiveUnplacedCriticalActionCount = 0;
    possiblePathNodes = nextPossiblePathNodes;
    nextPossiblePathNodes = [];
  }
  if (!possiblePathNodes.length) {
    // We have no idea where we are
    return null;
  }
  let mostLikelyPath = possiblePathNodes[0]; // all these paths are possible, arbitrarily pick the first one

  const previousMostLikelyPathTracingNode = previousPathTakenResult?.mostLikelyPathTaken;
  if (previousMostLikelyPathTracingNode) {
    // if there is a possible result which is similar to the previous calculation, use that result instead
    const previousCalculationLastSeenNodeId = previousMostLikelyPathTracingNode.node.id;
    for (let possiblePath of possiblePathNodes) {
      if (possiblePath.path.length !== previousMostLikelyPathTracingNode.path.length || possiblePath.node.id !== previousCalculationLastSeenNodeId) {
        continue;
      }
      mostLikelyPath = possiblePath;
      break;
    }
  }
  return {
    mostLikelyPathTaken: mostLikelyPath,
    allPossiblePaths: possiblePathNodes
  };
}

/**
 * startCondensedNodeAtAction - get fields for a PossibleTakenPath object which describes a possible path starting in the middle of a condensedNode at a specific action.
 *
 * @param {CondensedActionGraphNode<IdType>} condensedNode - the condensedNode to generate the information for
 * @param {string} startingAtAction - the action to consider the condensed node as starting at
 *
 * @return {{|
 *	startNodeAtTime: number,
 *	startNodeAtNotableEventIndex: number, // this can be out of range in the upper direction
 * |}}
 */
function startCondensedNodeAtAction(condensedNode, startingAtAction) {
  for (let actionData of condensedNode.actions) {
    if (String(actionData.actionId) !== startingAtAction) {
      continue;
    }
    let startNodeAtTime = actionData.startTime;
    let startIndex = condensedNode.notableEvents.findIndex(({
      atTime
    }) => atTime >= startNodeAtTime);
    if (startIndex === -1) {
      return {
        startNodeAtTime,
        startNodeAtNotableEventIndex: condensedNode.notableEvents.length
      };
    }
    return {
      startNodeAtTime,
      startNodeAtNotableEventIndex: startIndex
    };
  }

  // this should not happen
  return {
    // consider node as never starting
    startNodeAtTime: condensedNode.duration,
    startNodeAtNotableEventIndex: condensedNode.notableEvents.length
  };
}

/**
 * doesNotableEventsContainCheckpoint - checks if any of the notable events are a checkpoint
 *
 * @param {Array<NotableEvent<IdType>>} notableEvents - the list of notable events to check
 *
 * @return {boolean} - true if at least one of the notable events is a checkpoint, false otherwise
 */
function doesNotableEventsContainCheckpoint(notableEvents) {
  for (let notableEvent of notableEvents) {
    if (notableEvent.type === _notableEvents.NOTABLE_EVENT_TYPES.CHECKPOINT) {
      return true;
    }
  }
  return false;
}

/**
 * getCheckpointAndChildrenInformationForPathAtCondensedNode - get the children for a path currently at the given condensed node as well as the derived fields relating to checkpoints.
 *
 * @param {CondensedActionGraphNode<IdType>} node - the node the possible path is currently in
 * @param {?PossibleTakenPath<IdType>} comingFromPossiblePath - the path taken leading to this node
 * @param {CondensedNodeLookup} nodeLookup - a mapping of the condensedNode id to the condensed node with that id
 *
 * @return {{|
 *	containsCheckpoint: boolean, - true if he condensed node contains a checkpoint
 *	lastSeenCondensedNodeWithCheckpoint: string | null, - the last condensed node in the path taken which had a checkpoint
 *	possibleNextCondensedNodes: Array<CondensedActionGraphNode<IdType>>, - a list of possible condensed nodes which can come after the last condensed node in this path
 * |}}
 */
function getCheckpointAndChildrenInformationForPathAtCondensedNode(node, comingFromPossiblePath, nodeLookup) {
  const containsCheckpoint = doesNotableEventsContainCheckpoint(node.notableEvents);
  const lastSeenCondensedNodeWithCheckpoint = containsCheckpoint ? node.id : comingFromPossiblePath?.lastSeenCondensedNodeWithCheckpoint ?? null;
  const possibleNextCondensedNodes = [];
  for (let childData of node.children) {
    if (childData === node.id) {
      // ignore single cycles
      continue;
    }
    const childNode = nodeLookup[childData.childNodeId];
    if (!childNode) {
      continue;
    }
    possibleNextCondensedNodes.push(childNode);
  }
  if (node.meta.containsDeathAction && lastSeenCondensedNodeWithCheckpoint && lastSeenCondensedNodeWithCheckpoint !== node.id) {
    const checkpointNode = nodeLookup[lastSeenCondensedNodeWithCheckpoint];
    possibleNextCondensedNodes.push(checkpointNode);
  }
  return {
    containsCheckpoint,
    lastSeenCondensedNodeWithCheckpoint,
    possibleNextCondensedNodes
  };
}
/**
 * getLookaheadPath - get the pessimistic path (time wise) to the end of the mission running a simulation
 *
 * @param {CondensedActionGraph<IdType>} condensedActionGraph - the condensed action graph for the simulation the mission is running
 * @param {?PathTakenResult<IdType>} pathTaken - the path already in the simulation by the mission
 *
 * @return {?LookaheadPath<IdType>} - a possible pessimistic (time wise) path that will reach the end of the simulation
 */
function getLookaheadPath(condensedActionGraph, pathTaken) {
  const mostLikelyPathTaken = pathTaken?.mostLikelyPathTaken;
  if (!mostLikelyPathTaken?.path.length) {
    return null;
  }
  const nodeLookup = {};
  for (let condensedNode of condensedActionGraph.nodes) {
    nodeLookup[condensedNode.id] = condensedNode;
  }
  const currentlyInCondensedNodeId = mostLikelyPathTaken.path[mostLikelyPathTaken.path.length - 1];
  const startingCondensedNode = nodeLookup[currentlyInCondensedNodeId];
  if (!startingCondensedNode) {
    return null;
  }
  const currentActionStartTime = (mostLikelyPathTaken.lastSeenCriticalActionId ? mostLikelyPathTaken.node.actions[mostLikelyPathTaken.node.criticalPathActions[mostLikelyPathTaken.lastSeenCriticalActionId]] : null)?.startTime ?? 0;
  const pathAfterDeathCheckpointJumpBack = [];
  const pathBeforeDeath = [{
    nodeId: currentlyInCondensedNodeId,
    startTime: currentActionStartTime + 1 // + 1 sine we are currently at time `currentActionStartTime`, and this path represents what occurs after
  }];
  let alreadyJumpedBackToIndexInPathTaken = null; // null if we have not jumped back before, the index into the `pathTaken` if we jumped back before
  let stuckInADeathLoop = false;
  let pathToTake = pathBeforeDeath; // This will change from pointing to `pathBeforeDeath` to `pathAfterDeathCheckpointJumpBack` if death is encountered
  let currentNode = startingCondensedNode;
  let seenNodes = new Set();
  while (!currentNode.meta.containsEndAction) {
    let nextNodeId =
    // prefer traveling to end, only go to death if there is no way to the end
    currentNode.meta.pessimisticDurationUntilEnd?.nextNodeId ?? currentNode.meta.pessimisticDurationUntilDeath?.nextNodeId;
    if (
    // there is no path an end or death action
    !nextNodeId || seenNodes.has(nextNodeId) // we are in a loop
    ) {
      break;
    }
    seenNodes.add(nextNodeId);
    pathToTake.push({
      nodeId: nextNodeId,
      startTime: 0
    });
    currentNode = nodeLookup[nextNodeId];
    if (!currentNode) {
      break;
    }
    if (currentNode.meta.containsDeathAction) {
      if (alreadyJumpedBackToIndexInPathTaken != null) {
        // We are in an unescapable death loop - jumping back to a checkpoint before did not allow the mission to reach the end
        stuckInADeathLoop = true;
        break;
      }

      // go backwards until we hit a checkpoint
      for (let i = pathToTake.length - 1; i >= 0; i--) {
        const previousNodeNotableEvents = nodeLookup[pathToTake[i].nodeId].notableEvents;
        for (let j = previousNodeNotableEvents.length - 1; j >= 0; j--) {
          if (previousNodeNotableEvents[j].type === _notableEvents.NOTABLE_EVENT_TYPES.CHECKPOINT) {
            // the most recent checkpoint is guaranteed to only lead to a death again
            stuckInADeathLoop = true;
          }
        }
      }
      if (stuckInADeathLoop) {
        break;
      }

      // find the most recent checkpoint that the mission encountered and jump back to it
      for (let i = mostLikelyPathTaken.path.length - 1; i >= 0 && alreadyJumpedBackToIndexInPathTaken != null; i--) {
        const previousCondensedNode = nodeLookup[pathToTake[i].nodeId];
        if (!previousCondensedNode) {
          continue;
        }
        const previousNodeNotableEvents = previousCondensedNode?.notableEvents;
        for (let j = previousNodeNotableEvents.length - 1; j >= 0; j--) {
          if (previousNodeNotableEvents[j].type !== _notableEvents.NOTABLE_EVENT_TYPES.CHECKPOINT) {
            continue;
          }

          // Found the location to jump back to
          alreadyJumpedBackToIndexInPathTaken = i;
          pathAfterDeathCheckpointJumpBack.push({
            nodeId: previousCondensedNode.id,
            startTime: previousNodeNotableEvents[j].atTime // this is the time of the checkpoint, this gets updated later on to only include after the checkpoint
          });
          pathToTake = pathAfterDeathCheckpointJumpBack;

          // restart the graph traversal at the checkpoint location. This will not create an infinite loop as `alreadyJumpedBackToIndexInPathTaken` is set above.
          seenNodes = new Set([previousCondensedNode.id]);
          currentNode = previousCondensedNode;
          break;
        }
      }

      // there is no checkpoint to jump back to, assume the current path is the final path
      break;
    }
  }
  let nextActionInstanceId = 1;
  const getNextActionInstanceId = () => nextActionInstanceId++;
  const notableEventTimeline = [];
  let durationAlreadyTaken = 0;
  let continuePathUntilIndex = mostLikelyPathTaken.path.length - 1;
  let continuePathUntilStartTime = currentActionStartTime;
  if (alreadyJumpedBackToIndexInPathTaken != null && pathAfterDeathCheckpointJumpBack.length &&
  // if the above to condition pass, but this condition fails, then it means that the graph was populated wrong
  pathAfterDeathCheckpointJumpBack[0].nodeId === mostLikelyPathTaken.path[alreadyJumpedBackToIndexInPathTaken]) {
    continuePathUntilIndex = alreadyJumpedBackToIndexInPathTaken + 1;
    // if we had to jump back to a checkpoint, this condensed node would be partway through. Only consider the part of the condensed node which will still be completed after the jump back
    continuePathUntilStartTime = pathAfterDeathCheckpointJumpBack[0].startTime;
  }
  for (let i = 0; i <= continuePathUntilIndex; i++) {
    const condensedNodeId = mostLikelyPathTaken.path[i];
    let node = nodeLookup[condensedNodeId];
    if (!node) {
      continue;
    }
    if (i === continuePathUntilIndex) {
      const notableEventsThatWillRun = [];
      for (let notableEvent of node.notableEvents) {
        if (notableEvent.atTime > continuePathUntilStartTime) {
          break;
        }
        notableEventsThatWillRun.push(notableEvent);
      }
      notableEventTimeline.push(...updateNotableEventsStartTimesAndActionInstanceId(notableEventsThatWillRun, durationAlreadyTaken, getNextActionInstanceId));
      durationAlreadyTaken += continuePathUntilStartTime;
      break; // everything after this point will be handled by the lookahead path
    }

    // if we had to jump back to a checkpoint, this condensed node is still fully completed after the jump back
    notableEventTimeline.push(...updateNotableEventsStartTimesAndActionInstanceId(node.notableEvents, durationAlreadyTaken, getNextActionInstanceId));
    durationAlreadyTaken += node.duration;
    continue;
  }
  if (pathAfterDeathCheckpointJumpBack.length) {
    pathAfterDeathCheckpointJumpBack[0].startTime += 1; // + 1 this path needs to describe what happens after the checkpoint
  }
  let durationLeft = 0;
  if (alreadyJumpedBackToIndexInPathTaken != null && pathBeforeDeath.length) {
    // the time left estimate should include the time until death
    const firstNodeInDeathPath = pathBeforeDeath[0];
    const durationUntilDeath = nodeLookup[firstNodeInDeathPath.nodeId]?.meta.pessimisticDurationUntilDeath?.duration;
    if (
    // if this condition fails, then there is a bug in the graph generation since it should have been impossible for this node to reach a death
    durationUntilDeath != null) {
      durationLeft += durationUntilDeath - firstNodeInDeathPath.startTime;
    }
  }
  for (let lookaheadStep of pathToTake) {
    const node = nodeLookup[lookaheadStep.nodeId];
    if (!node) {
      continue;
    }
    const notableEventsHappeningAfterStartTime = [];
    for (let notableEvent of node.notableEvents) {
      if (notableEvent.atTime < lookaheadStep.startTime) {
        // we are starting this condensed node part way through (probably due to jumping to a checkpoint), as such, this notable event does not occur
        continue;
      }
      notableEventsHappeningAfterStartTime.push(notableEvent);
    }
    notableEventTimeline.push(...updateNotableEventsStartTimesAndActionInstanceId(notableEventsHappeningAfterStartTime, durationAlreadyTaken + durationLeft - lookaheadStep.startTime,
    // current condensed nodes may not start at the beginning move the "start" of the condensed node to the startTime,
    getNextActionInstanceId));
    durationLeft += node.duration - lookaheadStep.startTime;
  }
  return {
    pathToTake: pathToTake.map(({
      nodeId
    }) => nodeId),
    notableEventTimeline,
    timeAlreadyTaken: durationAlreadyTaken,
    expectedTimeLeft: durationLeft,
    lastSeenCriticalActionId: alreadyJumpedBackToIndexInPathTaken != null ? null // we jumped back to a checkpoint during the lookahead, so the lastSeenCriticalActionId could be in the future
    : mostLikelyPathTaken.lastSeenCriticalActionId
  };
}

/**
 * updateNotableEventsStartTimesAndActionInstanceId - update the start time of the notable events to translate them to the time they have, or expected to, run on the mission. Also updates the `actionInstanceId` to make
 * sure the notable events spawned by the same action have a unique actionInstanceId which is unique through the timeline.
 *
 * @param {Array<NotableEvent<IdType>>} notableEvents - the notable events in a condensed node
 * @param {number} condensedNodeStartTime - the time the condensed node has, or expects to, start during the mission
 * @param {() => number} getNextActionInstanceId - get the next action instance id
 *
 * @return {Array<NotableEvent<IdType>>} - a list of notable events with their atTimes now reflecting 0 being the start of the mission. Also updates the actionInstanceId.
 */
function updateNotableEventsStartTimesAndActionInstanceId(notableEvents, condensedNodeStartTime, getNextActionInstanceId) {
  const updatedNotableEvents = [];
  const actionInstanceIdUpdateMap = {};
  for (let notableEvent of notableEvents) {
    actionInstanceIdUpdateMap[notableEvent.actionInstanceId] ??= getNextActionInstanceId();
    updatedNotableEvents.push({
      ...notableEvent,
      actionInstanceId: actionInstanceIdUpdateMap[notableEvent.actionInstanceId],
      atTime: condensedNodeStartTime + notableEvent.atTime
    });
  }
  return updatedNotableEvents;
}